import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, ArrowRight, Download, Upload, FileText, CheckCircle, 
  Loader2, Building2, User, Mail, Phone, Globe, Linkedin, Briefcase,
  ArrowDown, Send, Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationSubmitPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [step, setStep] = useState(1); // 1: Download, 2: Fill Form, 3: Success
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    linkedin_url: '',
    founder_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    industry: '',
    stage: '',
    problem_statement: '',
    solution: '',
    target_market: '',
    team_size: '',
    traction: '',
    funding_stage: '',
    governance_focus: '',
    how_heard: ''
  });

  const industries = ["Climate Tech", "FinTech / RegTech", "Cross-Border Services", "HealthTech", "EdTech", "PropTech", "Other"];
  const stages = ["Idea", "MVP", "Early Revenue", "Growth"];
  const fundingStages = ["Bootstrapped", "Pre-seed", "Seed", "Series A", "Series A+"];

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/application-form/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'SKI_AXIS_Application_Form.doc');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloaded(true);
      toast({ title: "Form Downloaded!", description: "Fill out the form and submit below" });
    } catch (error) {
      toast({ title: "Download failed", description: "Please try again", variant: "destructive" });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.email || !formData.problem_statement) {
      toast({ title: "Required fields missing", description: "Please fill all required fields", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/application-form/submit`, formData);
      setStep(3);
      toast({ title: "Application Submitted!", description: "We'll review and get back to you soon" });
    } catch (error) {
      toast({ title: "Submission failed", description: error.response?.data?.detail || "Please try again", variant: "destructive" });
    }
    setLoading(false);
  };

  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <Header />
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Application Submitted!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for applying to SKI AXIS Accelerator. Our team will review your application and get back to you within 48 hours.
            </p>
            <Card className="bg-green-50 border-green-200 mb-8">
              <CardContent className="p-6">
                <h3 className="font-semibold text-green-800 mb-3">Next Steps:</h3>
                <ul className="text-left text-green-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>Nova AI will evaluate your application</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>You'll receive detailed feedback via email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>Check your <a href="https://docs.google.com/document/d/your-journey-doc" className="underline">Program Journey Document</a></span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/governance')} className="bg-gradient-to-r from-green-600 to-emerald-600">
                View Portfolio
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <Header />
      
      {/* Progress Steps */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>1</div>
              <span className="font-medium hidden sm:inline">Download Form</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-200"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>2</div>
              <span className="font-medium hidden sm:inline">Fill & Submit</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-200"></div>
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>3</div>
              <span className="font-medium hidden sm:inline">Complete</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        
        {/* Step 1: Download */}
        {step === 1 && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Apply to SKI AXIS Accelerator</h1>
            <p className="text-lg text-gray-600 mb-8">Download our application form, fill it out, and submit below</p>
            
            <Card className="max-w-md mx-auto mb-8 border-2 border-dashed border-green-300 bg-green-50/50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Form Template</h3>
                <p className="text-gray-600 mb-4">Word document (.doc) - Easy to fill</p>
                <Button 
                  onClick={handleDownload} 
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg"
                  data-testid="download-form-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Download Form
                </Button>
              </CardContent>
            </Card>
            
            {downloaded && (
              <div className="bg-green-100 border border-green-300 rounded-lg p-4 mb-6 inline-flex items-center gap-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                Form downloaded! Fill it out, then continue below.
              </div>
            )}
            
            <div className="flex items-center justify-center gap-2 text-gray-500 mb-4">
              <ArrowDown className="h-5 w-5 animate-bounce" />
              <span>Or fill out the online form directly</span>
              <ArrowDown className="h-5 w-5 animate-bounce" />
            </div>
            
            <Button 
              onClick={() => setStep(2)} 
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
              data-testid="continue-to-form-btn"
            >
              Continue to Online Form
            </Button>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && (
          <Card className="shadow-xl">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-gray-500">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              </div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-green-600" />
                Application Form
              </CardTitle>
              <CardDescription>Complete your application to join SKI AXIS Accelerator</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Company Info */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600" /> Company Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Your startup name" required data-testid="company-name" />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="pl-10" placeholder="https://..." />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label>LinkedIn Company Page</Label>
                      <div className="relative">
                        <Linkedin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} className="pl-10" placeholder="linkedin.com/company/..." />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Founder Info */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-green-600" /> Founder Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input value={formData.founder_name} onChange={(e) => setFormData({...formData, founder_name: e.target.value})} placeholder="John Smith" required data-testid="founder-name" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="pl-10" placeholder="you@company.com" required data-testid="email" />
                      </div>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="pl-10" placeholder="+971 50 123 4567" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-green-600" /> Business Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Industry *</Label>
                      <Select value={formData.industry} onValueChange={(v) => setFormData({...formData, industry: v})}>
                        <SelectTrigger data-testid="industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                        <SelectContent>
                          {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Stage</Label>
                      <Select value={formData.stage} onValueChange={(v) => setFormData({...formData, stage: v})}>
                        <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
                        <SelectContent>
                          {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Problem Statement *</Label>
                      <Textarea value={formData.problem_statement} onChange={(e) => setFormData({...formData, problem_statement: e.target.value})} placeholder="What problem are you solving?" className="min-h-24" required data-testid="problem" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Your Solution *</Label>
                      <Textarea value={formData.solution} onChange={(e) => setFormData({...formData, solution: e.target.value})} placeholder="How does your product/service solve this problem?" className="min-h-24" required />
                    </div>
                    <div>
                      <Label>Target Market</Label>
                      <Input value={formData.target_market} onChange={(e) => setFormData({...formData, target_market: e.target.value})} placeholder="Who are your customers?" />
                    </div>
                    <div>
                      <Label>Team Size</Label>
                      <Input type="number" value={formData.team_size} onChange={(e) => setFormData({...formData, team_size: e.target.value})} placeholder="Number of team members" />
                    </div>
                    <div>
                      <Label>Current Traction</Label>
                      <Input value={formData.traction} onChange={(e) => setFormData({...formData, traction: e.target.value})} placeholder="Users, revenue, partnerships..." />
                    </div>
                    <div>
                      <Label>Funding Stage</Label>
                      <Select value={formData.funding_stage} onValueChange={(v) => setFormData({...formData, funding_stage: v})}>
                        <SelectTrigger><SelectValue placeholder="Select funding stage" /></SelectTrigger>
                        <SelectContent>
                          {fundingStages.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Governance */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-green-600" /> Governance & Additional Info
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>What governance challenges are you facing?</Label>
                      <Textarea value={formData.governance_focus} onChange={(e) => setFormData({...formData, governance_focus: e.target.value})} placeholder="Compliance, board structure, risk management..." className="min-h-20" />
                    </div>
                    <div>
                      <Label>How did you hear about SKI AXIS?</Label>
                      <Input value={formData.how_heard} onChange={(e) => setFormData({...formData, how_heard: e.target.value})} placeholder="Referral, LinkedIn, Event..." />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full py-6 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-xl"
                  data-testid="submit-application-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ApplicationSubmitPage;
